import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { myPagePath } from '../utils/myPage';
import { getWithRetry, isNotFound, isRecipePrivate } from '../utils/fetchRetry';
import { getAuthConfig } from '../utils/authSession';
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Link, Clock, Users, ExternalLink } from "lucide-react";
import RecipeForm from '../components/RecipeForm';
import SaveCreditLine from '../components/SaveCreditLine';
import ProfileAvatar from '../components/ProfileAvatar';
import SortableCategoryItem from '../components/SortableCategoryItem';
import { API } from '../utils/constants';
import { trackShareClick } from '../utils/track';
import recipeCache from '../utils/recipeCache';
import { getCollectionDisplayTitle } from '../utils/collectionTitle';
import { isInstructionHeading, stripInstructionListMarker, hasSingleInstructionStep, withoutSingleStepNumber } from '../utils/quickImportFormatting';
import NotFound from './NotFound';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const RecipeDetail = ({ recipeId: recipeIdProp }) => {
  const { id: idParam } = useParams();
  const id = recipeIdProp || idParam;
  const navigate = useNavigate();
  const location = useLocation();
  // Start from the cache when the recipe is already in hand (e.g. right
  // after the pretty-URL swap remounts this component) — rendering it on the
  // first frame avoids a skeleton flash mid-navigation.
  const cachedInitialRecipe = id ? recipeCache.getById(id) : null;
  const [recipe, setRecipe] = useState(cachedInitialRecipe);
  const [loading, setLoading] = useState(!cachedInitialRecipe);
  const [notFound, setNotFound] = useState(false);
  const [privateRecipe, setPrivateRecipe] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  
  // Category management states
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState({ open: false, category: null, recipeCount: 0 });

  const fetchCategories = async (token) => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  };

  // Category management handlers
  const handleCreateCategory = async () => {
    if (!newCategoryInput.trim()) return;
    
    try {
      const response = await axios.post(
        `${API}/categories`,
        { name: newCategoryInput.trim() },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      setCategories([...categories, response.data]);
      setNewCategoryInput('');
      toast.success('Category created!');
    } catch (error) {
      console.error("Failed to create category", error);
      toast.error("Failed to create category");
    }
  };

  const handleUpdateCategory = async (categoryId, newName) => {
    if (!newName.trim()) return;
    
    try {
      await axios.put(
        `${API}/categories/${categoryId}`,
        { name: newName.trim() },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      setCategories(categories.map(cat => 
        cat.id === categoryId ? { ...cat, name: newName.trim() } : cat
      ));
      setEditingCategoryId(null);
      setEditingCategoryName('');
      toast.success('Category updated!');
    } catch (error) {
      console.error("Failed to update category", error);
      toast.error("Failed to update category");
    }
  };

  const handleDeleteCategory = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return;
    
    setDeleteCategoryDialog({
      open: true,
      category: category,
      recipeCount: 0
    });
  };

  const confirmDeleteCategory = async () => {
    const { category } = deleteCategoryDialog;
    if (!category) return;
    
    try {
      await axios.delete(
        `${API}/categories/${category.id}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      setCategories(categories.filter(cat => cat.id !== category.id));
      setDeleteCategoryDialog({ open: false, category: null, recipeCount: 0 });
      toast.success('Category deleted!');
      
      // Refresh recipe data
      const response = await axios.get(`${API}/recipes/${id}`);
      setRecipe(response.data);
    } catch (error) {
      console.error("Failed to delete category", error);
      toast.error("Failed to delete category");
      setDeleteCategoryDialog({ open: false, category: null, recipeCount: 0 });
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }
    
    const oldIndex = categories.findIndex((cat) => cat.id === active.id);
    const newIndex = categories.findIndex((cat) => cat.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const newCategories = arrayMove(categories, oldIndex, newIndex);
    setCategories(newCategories);
    
    try {
      await axios.put(
        `${API}/categories/reorder`,
        { category_ids: newCategories.map(cat => cat.id) },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Category order updated!');
    } catch (error) {
      console.error("Failed to update category order", error);
      toast.error("Failed to update category order");
      fetchCategories(adminToken);
    }
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    let cancelled = false;
    const initializeAuth = async () => {
      const authCfg = await getAuthConfig();
      if (cancelled || !authCfg) return;
      const token = authCfg.headers.Authorization.replace(/^Bearer\s+/, '');
      setAdminToken(token);
      fetchCategories(token);
      // Fetch user profile to get username for public links
      axios.get(`${API}/my-profile`, authCfg)
        .then(res => { if (!cancelled) setUserProfile(res.data); })
        .catch(() => {});
    };
    initializeAuth();
    
    // Check if we should start in edit mode (from URL params)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('edit') === 'true') {
      setIsEditMode(true);
    }
    return () => { cancelled = true; };
  }, []);

  // Fetch categories when manage dialog opens
  useEffect(() => {
    if (showManageCategories && adminToken) {
      fetchCategories(adminToken);
    }
  }, [showManageCategories, adminToken]);

  useEffect(() => {
    let cancelled = false;
    // Visitors who aren't signed in shouldn't see the private /recipe/:id view.
    // Redirect them to the pretty public link (/:username/:slug) when possible.
    const redirectVisitorToPublicLink = async (recipeData) => {
      try {
        const profileResponse = await axios.get(`${API}/profile?user_id=${recipeData.user_id}`);
        if (cancelled) return false;
        const username = profileResponse.data?.username;
        if (username && recipeData.slug) {
          // Seed the slug cache so the pretty-URL page can render this
          // recipe on its very first frame instead of flashing a skeleton
          // and re-fetching what we already have.
          recipeCache.set(recipeData, username);
          navigate(`/${username}/${recipeData.slug}`, { replace: true });
          return true;
        }
      } catch {
        // Fall through to showing the recipe here.
      }
      return false;
    };

    const fetchRecipe = async () => {
      // Fresh request — clear stale outcome state from a previous URL
      setNotFound(false);
      setPrivateRecipe(false);
      setLoadError(false);
      setLoading(true);
      const authCfg = await getAuthConfig();
      if (cancelled) return;
      const isSignedIn = !!authCfg;

      // Check cache first
      const cached = recipeCache.getById(id);
      if (cached) {
        if (!isSignedIn && await redirectVisitorToPublicLink(cached)) return;
        if (cancelled) return;
        setRecipe(cached);
        setLoading(false);
        return;
      }

      try {
        const response = await getWithRetry(`${API}/recipes/${id}`, authCfg);
        if (cancelled) return;
        if (!isSignedIn && await redirectVisitorToPublicLink(response.data)) return;
        if (cancelled) return;
        setRecipe(response.data);
        // Cache the recipe
        recipeCache.set(response.data);
      } catch (error) {
        if (cancelled) return;
        // Only a real 404 means the recipe doesn't exist; anything else
        // (network blip, server error) gets a retry screen instead.
        if (isRecipePrivate(error)) {
          recipeCache.invalidate(id);
          setRecipe(null);
          setPrivateRecipe(true);
        } else if (isNotFound(error)) {
          setNotFound(true);
        } else {
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRecipe();
    return () => { cancelled = true; };
  }, [id, navigate, retryKey]);

  // Signed-in owners visiting /recipe/:id also get the pretty public URL in
  // the address bar (the pretty route shows them this same owner view), so
  // copying the address bar always shares the clean link.
  useEffect(() => {
    if (recipeIdProp) return; // already rendering under the pretty URL
    if (!recipe || !userProfile) return;
    if (recipe.user_id !== userProfile.id) return;
    if (!userProfile.username || !recipe.slug) return;
    // Seed the slug cache so the pretty-URL page renders instantly (no
    // skeleton flash / re-fetch) right after the address bar swaps.
    recipeCache.set(recipe, userProfile.username);
    navigate(`/${userProfile.username}/${recipe.slug}${window.location.search}`, { replace: true });
  }, [recipe, userProfile, recipeIdProp, navigate]);

  const handleCopyLink = () => {
    const baseUrl = window.location.origin;
    if (userProfile?.username && recipe?.slug) {
      navigator.clipboard.writeText(`${baseUrl}/${userProfile.username}/${recipe.slug}`);
    } else {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.username && recipe?.slug) {
        navigator.clipboard.writeText(`${baseUrl}/${user.username}/${recipe.slug}`);
      } else {
        navigator.clipboard.writeText(window.location.href);
      }
    }
    trackShareClick();
    toast.success("Link copied to clipboard!");
  };

  const handleSaveRecipe = async (savedRecipe) => {
    setIsEditMode(false);
    // Invalidate cache since recipe was updated
    recipeCache.invalidate(id);
    if (savedRecipe && savedRecipe.id) {
      // Use the server's save response directly — refetching immediately
      // after a write can return stale data and undo the edit on screen.
      setRecipe(savedRecipe);
      recipeCache.set(savedRecipe);
    } else {
      // Fallback: refetch if no recipe was passed
      const response = await axios.get(`${API}/recipes/${id}`);
      setRecipe(response.data);
      recipeCache.set(response.data);
    }
    toast.success("Recipe updated successfully!");
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#EEE7D8' }}>
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back button skeleton */}
          <div className="h-5 w-32 rounded mb-6" style={{ backgroundColor: '#D6D0C2' }} />

          <div className="rounded-lg" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2' }}>
            {/* Image placeholder skeleton */}
            <div className="w-full h-80" style={{ backgroundColor: '#D6D0C2' }} />
            
            <div className="p-6">
              {/* Category tags skeleton */}
              <div className="flex gap-2 mb-4">
                <div className="h-6 w-16 rounded-full" style={{ backgroundColor: '#D6D0C2' }} />
                <div className="h-6 w-20 rounded-full" style={{ backgroundColor: '#D6D0C2' }} />
              </div>
              
              {/* Title skeleton */}
              <div className="h-9 w-3/4 rounded mb-2" style={{ backgroundColor: '#D6D0C2' }} />
              
              {/* Creator skeleton */}
              <div className="h-5 w-32 rounded mb-6" style={{ backgroundColor: '#D6D0C2' }} />
              
              {/* Meta info skeleton */}
              <div className="flex gap-6 mb-6">
                <div className="h-5 w-24 rounded" style={{ backgroundColor: '#D6D0C2' }} />
                <div className="h-5 w-24 rounded" style={{ backgroundColor: '#D6D0C2' }} />
              </div>
              
              {/* Description skeleton */}
              <div className="space-y-2 mb-8">
                <div className="h-4 w-full rounded" style={{ backgroundColor: '#D6D0C2' }} />
                <div className="h-4 w-5/6 rounded" style={{ backgroundColor: '#D6D0C2' }} />
              </div>
              
              {/* Ingredients section skeleton */}
              <div className="h-6 w-28 rounded mb-4" style={{ backgroundColor: '#D6D0C2' }} />
              <div className="space-y-2 mb-8">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-4 w-48 rounded" style={{ backgroundColor: '#D6D0C2' }} />
                ))}
              </div>
              
              {/* Instructions section skeleton */}
              <div className="h-6 w-28 rounded mb-4" style={{ backgroundColor: '#D6D0C2' }} />
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-4 w-full rounded" style={{ backgroundColor: '#D6D0C2' }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <NotFound
        title="Trouble loading this recipe"
        message="Your recipe is safe — we just couldn't reach it. Check your connection and try again."
        onRetry={() => {
          setLoadError(false);
          setLoading(true);
          setRetryKey((k) => k + 1);
        }}
      />
    );
  }

  if (privateRecipe) {
    const signedIn = !!localStorage.getItem('adminToken');
    const returnTo = location.pathname + location.search;
    return (
      <NotFound
        title="Oops, this recipe is private and can only be viewed when signed in!"
        message={signedIn
          ? "Only the account that owns this recipe can view it. Sign in with the owner account to continue."
          : "If it’s yours, sign in with the account that owns it"}
        actionLabel="Sign In"
        onAction={() => navigate(`/?login=true&returnTo=${encodeURIComponent(returnTo)}`)}
      />
    );
  }

  if (notFound || !recipe) {
    return (
      <NotFound
        title="Recipe not found"
        message="This recipe doesn't exist or may have been deleted."
      />
    );
  }

  const renderInstructions = () => {
    if (hasSingleInstructionStep(recipe.instructions)) {
      return <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} className="whitespace-pre-line" data-testid="recipe-instructions-paragraph">{withoutSingleStepNumber(recipe.instructions)}</p>;
    }
    if (recipe.instructions_type === "numbered") {
      const steps = recipe.instructions.split("\n").filter(s => s.trim());
      let stepNumber = 0;
      return (
        <ol className="list-decimal list-inside space-y-2" data-testid="recipe-instructions-numbered" style={{ color: '#51120A' }}>
          {steps.map((step, index) => {
            const isHeading = isInstructionHeading(step);
            if (!isHeading) stepNumber += 1;
            return (
              <li
                key={index}
                className={isHeading ? "list-none font-semibold" : undefined}
                value={isHeading ? undefined : stepNumber}
                style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              >
                {stripInstructionListMarker(step)}
              </li>
            );
          })}
        </ol>
      );
    }
    return <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} className="whitespace-pre-line" data-testid="recipe-instructions-paragraph">{recipe.instructions}</p>;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EEE7D8' }} data-testid="recipe-detail-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Link: your headshot + your page name (replaces the generic
            "View All Recipes"). Scroll position on the collection page is
            restored by listReturnState, so this lands on the exact spot. */}
        <button
          onClick={() => navigate(userProfile?.username ? `/${encodeURIComponent(userProfile.username)}` : myPagePath())}
          className="text-link mb-6 inline-flex items-center gap-2 text-base"
          data-testid="back-to-recipes-btn"
          style={{ fontFamily: "'Bricolage Grotesque', sans-serif", background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <ProfileAvatar
            photoUrl={userProfile?.creator_photo_url}
            name={userProfile?.creator_name || userProfile?.first_name || userProfile?.username || recipe?.creator}
            testId="back-to-recipes-avatar"
          />
          {userProfile ? getCollectionDisplayTitle(userProfile) : 'Your Recipes'}
        </button>

        {/* Recipe Card */}
        <Card className="overflow-hidden" style={{ backgroundColor: isEditMode ? '#F3EEE4' : '#F3EEE4', boxShadow: 'none', border: '1px solid #D6D0C2' }}>
          {!isEditMode && recipe.image_url && (
            <div className="w-full h-80 overflow-hidden">
              <img 
                src={recipe.image_url} 
                alt={recipe.title}
                className="w-full h-full object-cover"
                data-testid="recipe-detail-image"
              />
            </div>
          )}

          <CardContent className={`p-8 ${isEditMode ? 'recipe-edit-form' : ''}`}>
            {!isEditMode ? (
              <>
                {/* View Mode - Header */}
                <div className="mb-6 flex flex-col gap-5 md:flex-row md:items-start md:gap-12">
                  <div className="order-last min-w-0 flex-1 md:order-none">
                    <h1 className="mb-3 break-words text-4xl font-bold" style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }} data-testid="recipe-detail-title">{recipe.title}</h1>
                    {recipe.categories && recipe.categories.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {recipe.categories.map((cat, index) => (
                          <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: '#C9D7E9', color: '#0F2444' }} data-testid={`recipe-detail-category-${index}`}>
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Lead with where it was saved from; the original author follows */}
                    <SaveCreditLine recipe={recipe} placement="detail" />
                    {recipe.creator && (
                      <p className="text-lg" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="recipe-detail-creator">
                        {(recipe.saved_from_user_id && recipe.saved_from_name) ? 'Originally by' : 'by'} {recipe.creator}
                      </p>
                    )}
                  </div>
                  <div className="order-first flex w-full flex-col gap-2 md:order-none md:w-auto md:flex-shrink-0 md:flex-row">
                    {adminToken && (
                      <Button 
                        variant="outline"
                        onClick={() => setIsEditMode(true)} 
                        data-testid="recipe-detail-edit-btn"
                        className="w-full md:w-auto hover-blush transition-colors focus:outline-none focus:ring-0"
                        style={{ borderWidth: '1px', borderColor: '#51120A', color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                      >
                        Edit
                      </Button>
                    )}
                    {recipe.is_public === true ? (
                      <Button
                        onClick={handleCopyLink}
                        data-testid="recipe-detail-copy-btn"
                        className="w-full md:w-auto flex items-center justify-center gap-2"
                        style={{ backgroundColor: '#51120A', borderColor: '#51120A', color: 'white', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                      >
                        <Link className="h-4 w-4" />
                        Copy Link
                      </Button>
                    ) : (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              onClick={() => toast.info("Make recipe public to share")}
                              data-testid="recipe-detail-copy-btn"
                              className="w-full md:w-auto flex items-center justify-center gap-2"
                              style={{
                                backgroundColor: '#51120A',
                                borderColor: '#51120A',
                                color: 'white',
                                fontFamily: "'Bricolage Grotesque', sans-serif",
                                opacity: 0.5,
                                cursor: 'not-allowed',
                              }}
                            >
                              <Link className="h-4 w-4" />
                              Copy Link
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" sideOffset={8} className="bg-[#51120A] text-white">
                            Make recipe public to share
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>

                {/* Meta Information */}
                {(recipe.prep_time || recipe.cook_time || recipe.servings) && (
                  <div className="flex flex-wrap gap-6 mb-8 pb-8 border-b" style={{ borderColor: '#D6D0C2' }} data-testid="recipe-meta-info">
                    {recipe.prep_time && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5" style={{ color: '#51120A' }} />
                        <div>
                          <p className="text-xs" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Prep Time</p>
                          <p className="font-medium" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="recipe-detail-prep-time">{recipe.prep_time}</p>
                        </div>
                      </div>
                    )}
                    {recipe.cook_time && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5" style={{ color: '#51120A' }} />
                        <div>
                          <p className="text-xs" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Cook Time</p>
                          <p className="font-medium" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="recipe-detail-cook-time">{recipe.cook_time}</p>
                        </div>
                      </div>
                    )}
                    {recipe.servings && (
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5" style={{ color: '#51120A' }} />
                        <div>
                          <p className="text-xs" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Servings</p>
                          <p className="font-medium" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="recipe-detail-servings">{recipe.servings}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Ingredients */}
                {recipe.ingredients && recipe.ingredients.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4 style={{ color: '#51120A' }}">Ingredients</h2>
                    <div data-testid="recipe-detail-ingredients">
                      {recipe.ingredients.map((ingredient, index) => {
                        if (!ingredient || ingredient.trim() === "") {
                          return <div key={index} className="h-3" />;
                        }
                        const cleaned = ingredient.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '');
                        const isHeader = /.*:\s*$/.test(cleaned.trim()) && !/^\d/.test(cleaned.trim());
                        if (isHeader) {
                          return (
                            <p key={index} className="font-semibold mt-4 mb-1" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                              {cleaned.trim()}
                            </p>
                          );
                        }
                        return (
                          <div key={index} className="flex items-start py-0.5">
                            <span className="inline-block rounded-full mt-2 mr-3 flex-shrink-0" style={{ width: '6px', height: '6px', backgroundColor: '#51120A' }}></span>
                            <span style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>{cleaned}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Instructions */}
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4 style={{ color: '#51120A' }}">Instructions</h2>
                  {renderInstructions()}
                </div>

                {/* Notes */}
                {recipe.notes && (
                  <div className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Notes</h2>
                    <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} className="whitespace-pre-line" data-testid="recipe-notes">{recipe.notes}</p>
                  </div>
                )}

                {/* Source Link */}
                {recipe.source_link && (
                  <div className="pt-6 border-t" style={{ borderColor: '#D6D0C2' }}>
                    <a 
                      href={recipe.source_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-link inline-flex items-center"
                      data-testid="recipe-detail-source-link"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Original Source
                    </a>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Edit Mode - Use RecipeForm */}
                <RecipeForm
                  recipe={recipe}
                  onSave={handleSaveRecipe}
                  onCancel={() => setIsEditMode(false)}
                  adminToken={adminToken}
                  isEditing={true}
                  categories={categories}
                  onManageCategories={() => {
                    setShowManageCategories(true);
                    fetchCategories(adminToken);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Manage Categories Dialog */}
      <Dialog open={showManageCategories} onOpenChange={(open) => {
        setShowManageCategories(open);
        if (!open && adminToken) {
          // Refresh categories when dialog closes
          fetchCategories(adminToken);
        }
      }}>
        <DialogContent className="sm:max-w-[500px]" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif", fontSize: '32px', lineHeight: 1.1 }}>
              Manage Categories
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Add New Category Input */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Add New Category
              </label>
              <div className="flex gap-2">
                <Input
                  value={newCategoryInput}
                  onChange={(e) => setNewCategoryInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateCategory();
                    }
                  }}
                  placeholder="Type and press Enter"
                  className="flex-1"
                  style={{ 
                    backgroundColor: '#EEE7D8',
                    borderColor: '#D6D0C2',
                    color: '#51120A',
                    fontFamily: "'Bricolage Grotesque', sans-serif"
                  }}
                />
              </div>
              <p className="text-sm mt-1" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", opacity: 0.7 }}>
                Press Enter to create a new category
              </p>
            </div>

            {/* Categories List */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Your Categories
              </label>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={categories.map(cat => cat.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {categories.map((category) => (
                      <SortableCategoryItem 
                        key={category.id} 
                        category={category}
                        editingCategoryId={editingCategoryId}
                        editingCategoryName={editingCategoryName}
                        setEditingCategoryId={setEditingCategoryId}
                        setEditingCategoryName={setEditingCategoryName}
                        handleUpdateCategory={handleUpdateCategory}
                        handleDeleteCategory={handleDeleteCategory}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setShowManageCategories(false)}
              style={{ backgroundColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation Dialog */}
      <Dialog open={deleteCategoryDialog.open} onOpenChange={(open) => !open && setDeleteCategoryDialog({ open: false, category: null, recipeCount: 0 })}>
        <DialogContent className="sm:max-w-[425px]" overlayClassName="bg-[#EEE7D8]/60 backdrop-blur-sm" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif", fontSize: '24px' }}>
              Delete Category
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {deleteCategoryDialog.recipeCount > 0 ? (
              <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", lineHeight: '1.6' }}>
                Are you sure you want to delete this tag? This tag is present on{' '}
                <strong>{deleteCategoryDialog.recipeCount}</strong> recipe{deleteCategoryDialog.recipeCount > 1 ? 's' : ''}.
                <br /><br />
                Recipes will be moved to "uncategorized".
              </p>
            ) : (
              <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", lineHeight: '1.6' }}>
                Are you sure you want to delete this category?
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteCategoryDialog({ open: false, category: null, recipeCount: 0 })}
              style={{ 
                borderWidth: '1px',
                borderColor: '#51120A', 
                color: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
              className="hover-blush transition-colors"
            >
              No
            </Button>
            <Button
              onClick={confirmDeleteCategory}
              style={{ 
                backgroundColor: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
            >
              Yes, Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecipeDetail;
